import React, { useEffect, useCallback, useRef, useState } from 'react'

// Aggressive mobile performance monitoring and optimization
export const useAggressiveMobileOptimizations = () => {
  const [isLowMemoryDevice, setIsLowMemoryDevice] = useState(false)
  const [shouldDisableAnimations, setShouldDisableAnimations] = useState(false)
  const frameCount = useRef(0)
  const lastFrameTime = useRef(performance.now())
  const animationFrameId = useRef<number | null>(null)

  useEffect(() => {
    // Detect low memory devices
    const detectLowMemoryDevice = () => {
      const deviceMemory = (navigator as any).deviceMemory
      const hardwareConcurrency = navigator.hardwareConcurrency || 1
      
      // Consider low memory if:
      // - Device memory < 4GB
      // - CPU cores <= 2
      // - iOS Safari (often has aggressive memory limits)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      const isLowMem = deviceMemory && deviceMemory < 4
      const isLowCPU = hardwareConcurrency <= 2
      
      const isLowDevice = isLowMem || isLowCPU || isIOS
      
      setIsLowMemoryDevice(isLowDevice)
      
      if (isLowDevice) {
        console.warn('Low memory device detected, enabling aggressive optimizations')
      }
      
      return isLowDevice
    }

    // Monitor frame rate to detect performance issues
    const monitorFrameRate = () => {
      const now = performance.now()
      const delta = now - lastFrameTime.current
      lastFrameTime.current = now
      frameCount.current++

      // If frame time is consistently > 33ms (30fps), disable animations
      if (delta > 33 && frameCount.current > 60) {
        if (!shouldDisableAnimations) {
          console.warn('Poor frame rate detected, disabling animations')
          setShouldDisableAnimations(true)
        }
      }

      // Reset counter every 3 seconds
      if (frameCount.current > 180) {
        frameCount.current = 0
        // Re-enable animations after a break if performance improves
        if (shouldDisableAnimations && delta < 20) {
          setShouldDisableAnimations(false)
        }
      }

      animationFrameId.current = requestAnimationFrame(monitorFrameRate)
    }

    const isLowDevice = detectLowMemoryDevice()
    
    // Only monitor frame rate on low-end devices
    if (isLowDevice) {
      animationFrameId.current = requestAnimationFrame(monitorFrameRate)
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
      }
    }
  }, [shouldDisableAnimations])

  return {
    isLowMemoryDevice,
    shouldDisableAnimations
  }
}

// Cleanup DOM nodes aggressively
export const useDOMCleanup = () => {
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  useEffect(() => {
    const aggressiveCleanup = () => {
      // Remove disconnected nodes
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_ELEMENT,
        {
          acceptNode: (node) => {
            const element = node as Element
            // Check if element is invisible and can be safely removed
            if ((element as HTMLElement).offsetParent === null && 
                !element.classList.contains('fixed') &&
                !element.classList.contains('absolute') &&
                !element.hasAttribute('aria-hidden')) {
              return NodeFilter.FILTER_ACCEPT
            }
            return NodeFilter.FILTER_REJECT
          }
        }
      )

      const nodesToRemove: Element[] = []
      let node: Element | null
      
      while (node = walker.nextNode() as Element) {
        nodesToRemove.push(node)
      }

      // Remove invisible nodes (but be conservative)
      nodesToRemove.slice(0, 10).forEach(node => {
        try {
          if (node.parentNode && !node.querySelector('[data-keep]')) {
            node.parentNode.removeChild(node)
          }
        } catch (e) {
          // Node might already be removed
        }
      })

      // Clear any orphaned event listeners
      const elements = document.querySelectorAll('[data-cleanup]')
      elements.forEach(el => {
        if (!(el as any).isConnected) {
          el.remove()
        }
      })
    }

    // Run cleanup every 30 seconds on mobile
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      cleanupIntervalRef.current = setInterval(aggressiveCleanup, 30000)
    }

    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current)
      }
    }
  }, [])
}

// Memory pressure handling
export const useMemoryPressureHandling = () => {
  const [memoryPressure, setMemoryPressure] = useState(false)

  useEffect(() => {
    let pressureTimeout: NodeJS.Timeout

    const handleMemoryPressure = () => {
      setMemoryPressure(true)
      
      // Clear pressure flag after 10 seconds
      clearTimeout(pressureTimeout)
      pressureTimeout = setTimeout(() => {
        setMemoryPressure(false)
      }, 10000)

      // Immediate cleanup actions
      performEmergencyCleanup()
    }

    const performEmergencyCleanup = () => {
      try {
        // Clear image caches
        const images = document.querySelectorAll('img')
        images.forEach(img => {
          if (!img.hasAttribute('data-critical')) {
            img.removeAttribute('src')
            img.removeAttribute('srcset')
          }
        })

        // Remove non-critical elements temporarily
        const nonCritical = document.querySelectorAll('[data-non-critical]')
        nonCritical.forEach(el => {
          (el as HTMLElement).style.display = 'none'
        })

        // Clear any cached data
        try {
          sessionStorage.removeItem('highpoint_heavy_data')
          localStorage.removeItem('highpoint_animations_cache')
        } catch (e) {
          // Storage access failed
        }

        // Force garbage collection if available
        if ('gc' in window) {
          (window as any).gc()
        }

        console.warn('Emergency memory cleanup performed')
      } catch (e) {
        console.error('Emergency cleanup failed:', e)
      }
    }

    // Listen for page visibility changes (backgrounding can trigger memory pressure)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page going to background, preemptive cleanup
        setTimeout(performEmergencyCleanup, 1000)
      }
    }

    // Check for memory API and add listeners
    if ('memory' in performance) {
      const checkMemory = () => {
        const memInfo = (performance as any).memory
        const used = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit
        
        if (used > 0.9) {
          handleMemoryPressure()
        }
      }

      const memoryCheckInterval = setInterval(checkMemory, 5000)
      
      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        clearInterval(memoryCheckInterval)
        clearTimeout(pressureTimeout)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearTimeout(pressureTimeout)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return { memoryPressure }
}

// Optimized component wrapper for mobile
export const MobileOptimizedWrapper: React.FC<{
  children: React.ReactNode
  priority?: 'high' | 'medium' | 'low'
  className?: string
}> = ({ children, priority = 'medium', className }) => {
  const { isLowMemoryDevice, shouldDisableAnimations } = useAggressiveMobileOptimizations()
  const { memoryPressure } = useMemoryPressureHandling()
  const [shouldRender, setShouldRender] = useState(priority === 'high')

  useDOMCleanup()

  useEffect(() => {
    if (priority === 'high') {
      setShouldRender(true)
      return
    }

    // For medium/low priority content, delay rendering on low-end devices
    if (isLowMemoryDevice) {
      const delay = priority === 'medium' ? 1000 : 3000
      const timer = setTimeout(() => setShouldRender(true), delay)
      return () => clearTimeout(timer)
    } else {
      setShouldRender(true)
    }
  }, [isLowMemoryDevice, priority])

  // Don't render low priority content during memory pressure
  if (memoryPressure && priority === 'low') {
    return null
  }

  // Don't render at all if we shouldn't
  if (!shouldRender) {
    return <div className={`${className} min-h-[200px]`} data-loading="true" />
  }

  return (
    <div 
      className={className}
      data-priority={priority}
      data-disable-animations={shouldDisableAnimations}
      data-cleanup="true"
    >
      {children}
    </div>
  )
}

MobileOptimizedWrapper.displayName = 'MobileOptimizedWrapper'